(() => {
  const instances = new WeakMap();
  const mediaInstances = new WeakMap();
  const mediaOpenInstances = new WeakMap();
  const startedVideos = new WeakSet();
  const mediaQuery = window.matchMedia('(max-width: 760px)');

  const isElement = (value) => value?.nodeType === 1;
  const isTag = (value, tagName) => isElement(value) && value.tagName === tagName;
  const isVideo = (value) => isTag(value, 'VIDEO');
  const isImage = (value) => isTag(value, 'IMG');
  const isButton = (value) => isTag(value, 'BUTTON');
  const isDialog = (value) => isTag(value, 'DIALOG');
  const addMediaQueryListener = (listener) => {
    if (typeof mediaQuery.addEventListener === 'function') mediaQuery.addEventListener('change', listener);
    else if (typeof mediaQuery.addListener === 'function') mediaQuery.addListener(listener);
  };
  const removeMediaQueryListener = (listener) => {
    if (typeof mediaQuery.removeEventListener === 'function') mediaQuery.removeEventListener('change', listener);
    else if (typeof mediaQuery.removeListener === 'function') mediaQuery.removeListener(listener);
  };

  const getLabel = (name, fallback) => document.body.dataset[name] || fallback;

  const setMediaState = (target, state) => {
    if (!isElement(target)) return;
    target.classList.toggle('is-loading', state === 'loading');
    target.classList.toggle('is-playing', state === 'playing');
    target.setAttribute('aria-busy', String(state === 'loading'));
  };

  const getMediaRoot = (asset) => asset.closest('[data-responsive-media], [data-pdp-video]');

  const updateVideoToggle = (root, video) => {
    const button = root?.querySelector('[data-video-play]');
    if (!isButton(button)) return;
    const isPlaying = !video.paused && !video.ended;
    button.setAttribute('aria-label', getLabel(isPlaying ? 'mediaPauseLabel' : 'mediaPlayLabel', isPlaying ? 'Pause video' : 'Play video'));
    button.setAttribute('aria-pressed', String(isPlaying));
  };

  const handleVideoPlayback = (video, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (video.paused || video.ended) {
      startedVideos.add(video);
      loadAndPlay(video);
      return;
    }
    video.pause();
  };

  const bindMediaOpen = (asset) => {
    const canOpenImage = isImage(asset) && asset.closest('[data-media-gallery]');
    if (!canOpenImage || mediaOpenInstances.has(asset)) return;
    const handleClick = (event) => {
      event.preventDefault();
      openMediaDialog(asset, asset);
    };
    asset.addEventListener('click', handleClick);
    mediaOpenInstances.set(asset, handleClick);
  };

  const unbindMediaOpen = (asset) => {
    const handleClick = mediaOpenInstances.get(asset);
    if (!handleClick) return;
    asset.removeEventListener('click', handleClick);
    mediaOpenInstances.delete(asset);
  };

  const loadAndPlay = (video, stateTarget = getMediaRoot(video)) => {
    if (!isVideo(video)) return;

    video.autoplay = false;
    video.loop = true;
    video.muted = false;
    video.defaultMuted = false;
    video.removeAttribute('muted');
    video.volume = 1;
    video.playsInline = true;
    video.preload = 'auto';
    setMediaState(stateTarget, 'loading');

    if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
    const promise = video.play();
    if (promise?.catch) {
      promise.catch(() => {
        startedVideos.delete(video);
        setMediaState(stateTarget, 'paused');
        updateVideoToggle(stateTarget, video);
      });
    }
  };

  const bindVideo = (video) => {
    if (!isVideo(video) || mediaInstances.has(video)) return;

    const root = getMediaRoot(video);
    const handlePlaying = () => {
      setMediaState(root, 'playing');
      updateVideoToggle(root, video);
    };
    const handlePause = () => {
      if (!video.ended) setMediaState(root, 'paused');
      updateVideoToggle(root, video);
    };
    const handleWaiting = () => {
      if (!video.paused) setMediaState(root, 'loading');
    };
    const handleError = () => {
      setMediaState(root, 'paused');
      updateVideoToggle(root, video);
    };
    const handleClick = (event) => handleVideoPlayback(video, event);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleWaiting);
    video.addEventListener('error', handleError);
    video.addEventListener('click', handleClick);
    mediaInstances.set(video, { root, handlePlaying, handlePause, handleWaiting, handleError, handleClick });
    updateVideoToggle(root, video);
  };

  const unbindVideo = (video) => {
    const binding = mediaInstances.get(video);
    if (!binding) return;
    video.removeEventListener('playing', binding.handlePlaying);
    video.removeEventListener('pause', binding.handlePause);
    video.removeEventListener('waiting', binding.handleWaiting);
    video.removeEventListener('stalled', binding.handleWaiting);
    video.removeEventListener('error', binding.handleError);
    video.removeEventListener('click', binding.handleClick);
    mediaInstances.delete(video);
  };

  const getMediaDialog = () => {
    let dialog = document.querySelector('[data-global-media-dialog]');
    if (isDialog(dialog)) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'responsive-media__dialog';
    dialog.dataset.globalMediaDialog = '';
    dialog.setAttribute('aria-label', getLabel('mediaDialogGalleryLabel', 'Media gallery'));
    dialog.innerHTML = `
      <div class="responsive-media__dialog-inner">
        <div class="responsive-media__dialog-toolbar">
          <span class="responsive-media__dialog-counter" aria-live="polite"></span>
          <button class="responsive-media__dialog-close" type="button" aria-label="${getLabel('mediaDialogCloseLabel', 'Close')}"><span aria-hidden="true">×</span></button>
        </div>
        <div class="responsive-media__dialog-stage">
          <button class="responsive-media__dialog-nav responsive-media__dialog-nav--previous" type="button" aria-label="${getLabel('mediaDialogPreviousLabel', 'Previous media')}"><span aria-hidden="true">←</span></button>
          <div class="responsive-media__dialog-output"></div>
          <button class="responsive-media__dialog-nav responsive-media__dialog-nav--next" type="button" aria-label="${getLabel('mediaDialogNextLabel', 'Next media')}"><span aria-hidden="true">→</span></button>
        </div>
        <div class="responsive-media__dialog-thumbs" role="tablist" aria-label="${getLabel('mediaDialogGalleryLabel', 'Media gallery')}"></div>
      </div>`;
    document.body.append(dialog);

    const close = () => {
      if (dialog.open) dialog.close();
    };
    dialog.querySelector('.responsive-media__dialog-close')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('video').forEach((video) => video.pause());
      dialog.querySelector('.responsive-media__dialog-output')?.replaceChildren();
    });
    return dialog;
  };

  const getMediaGallery = (asset) => {
    const media = getMediaRoot(asset);
    return media?.closest('.cvid-grid, .main-product__videos, [data-media-gallery], [data-product-track], section') || media || asset;
  };

  const getMediaEntries = (gallery) => {
    if (!isElement(gallery)) return [];
    return [...new Set([
      ...gallery.querySelectorAll('[data-responsive-media] video, [data-responsive-media] img, [data-pdp-video] video')
    ])].filter((entry) => {
      if (!isImage(entry)) return true;
      return !entry.closest('video, [data-video-play], .responsive-media__video-play-button');
    });
  };

  const getMediaLabel = (asset, index) => {
    const label = asset.getAttribute('aria-label')
      || asset.getAttribute('alt')
      || asset.closest('.cvid-card, .cvid-item, [data-pdp-video]')?.querySelector('h1, h2, h3, h4, p')?.textContent?.trim();
    return label || `Media ${index + 1}`;
  };

  const removeDialogPlayControls = (output) => {
    output.querySelectorAll('[data-video-play], .responsive-media__video-play-button').forEach((control) => control.remove());
  };

  const openMediaDialog = (asset, opener = null) => {
    const dialog = getMediaDialog();
    const output = dialog.querySelector('.responsive-media__dialog-output');
    const counter = dialog.querySelector('.responsive-media__dialog-counter');
    const previousButton = dialog.querySelector('.responsive-media__dialog-nav--previous');
    const nextButton = dialog.querySelector('.responsive-media__dialog-nav--next');
    const thumbs = dialog.querySelector('.responsive-media__dialog-thumbs');
    const counterTemplate = getLabel('mediaDialogCounterTemplate', 'Media __INDEX__ of __TOTAL__');
    if (!isElement(output) || !isElement(counter) || !isElement(thumbs)) return;

    const gallery = getMediaGallery(asset);
    const entries = getMediaEntries(gallery);
    const thumbEntries = entries.filter(isImage);
    const initialIndex = Math.max(0, entries.indexOf(asset));
    let activeIndex = initialIndex;

    const updateCounter = () => {
      counter.textContent = counterTemplate
        .replace('__INDEX__', String(activeIndex + 1))
        .replace('__TOTAL__', String(entries.length));
    };

    const renderThumbs = () => {
      thumbs.replaceChildren();
      thumbEntries.forEach((entry) => {
        const entryIndex = entries.indexOf(entry);
        const button = document.createElement('button');
        button.className = 'responsive-media__dialog-thumb';
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-label', getMediaLabel(entry, entryIndex));
        button.setAttribute('aria-selected', String(entryIndex === activeIndex));
        button.dataset.mediaDialogIndex = String(entryIndex);

        const image = entry.cloneNode(true);
        image.className = 'responsive-media__dialog-thumb-image';
        image.removeAttribute('id');
        image.removeAttribute('loading');
        image.alt = '';
        button.append(image);
        button.addEventListener('click', () => renderEntry(entryIndex));
        thumbs.append(button);
      });
    };

    const renderEntry = (index) => {
      if (!entries.length) return;
      activeIndex = (index + entries.length) % entries.length;
      const entry = entries[activeIndex];
      output.replaceChildren();
      output.classList.remove('is-loading');
      output.setAttribute('aria-busy', 'false');

      if (isVideo(entry)) {
        const resumeAt = entry.currentTime;
        entry.pause();
        const clone = entry.cloneNode(true);
        clone.controls = true;
        clone.setAttribute('controls', 'controls');
        clone.autoplay = false;
        clone.loop = true;
        clone.muted = false;
        clone.defaultMuted = false;
        clone.removeAttribute('muted');
        clone.volume = 1;
        clone.playsInline = true;
        clone.preload = 'auto';
        clone.classList.add('responsive-media__dialog-video');
        clone.setAttribute('aria-label', getMediaLabel(entry, activeIndex));
        clone.addEventListener('loadedmetadata', () => {
          if (Number.isFinite(resumeAt) && resumeAt > 0 && resumeAt < clone.duration) clone.currentTime = resumeAt;
        }, { once: true });
        clone.addEventListener('playing', () => setMediaState(output, 'playing'), { once: true });
        output.classList.add('is-loading');
        output.setAttribute('aria-busy', 'true');
        output.append(clone);
        removeDialogPlayControls(output);
        loadAndPlay(clone, output);
      } else {
        const clone = entry.cloneNode(true);
        clone.className = 'responsive-media__dialog-image';
        clone.removeAttribute('id');
        output.append(clone);
        removeDialogPlayControls(output);
      }

      counter.textContent = counterTemplate
        .replace('__INDEX__', String(activeIndex + 1))
        .replace('__TOTAL__', String(entries.length));
      previousButton?.toggleAttribute('hidden', entries.length < 2);
      nextButton?.toggleAttribute('hidden', entries.length < 2);
      thumbs.querySelectorAll('[data-media-dialog-index]').forEach((thumb) => {
        const isActive = Number(thumb.dataset.mediaDialogIndex) === activeIndex;
        thumb.setAttribute('aria-selected', String(isActive));
        thumb.classList.toggle('is-active', isActive);
      });
    };

    previousButton.onclick = () => renderEntry(activeIndex - 1);
    nextButton.onclick = () => renderEntry(activeIndex + 1);
    dialog.onkeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        renderEntry(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        renderEntry(activeIndex + 1);
      }
    };
    dialog.onclose = () => {
      if (isElement(opener) && opener.isConnected) opener.focus();
    };

    renderThumbs();
    renderEntry(initialIndex);
    if (typeof dialog.showModal === 'function') dialog.showModal();
  };

  const createButton = (element, selector, className, label, content) => {
    let button = element.querySelector(selector);
    if (isButton(button)) return button;
    button = document.createElement('button');
    button.className = className;
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.innerHTML = content;
    if (selector === '[data-video-play]') button.dataset.videoPlay = '';
    if (selector === '[data-video-expand]') button.dataset.videoExpand = '';
    element.append(button);
    return button;
  };

  const syncMediaControls = (element) => {
    const video = element.querySelector('video');
    const image = element.querySelector('img');
    if (!isVideo(video) && !isImage(image)) return;

    element.classList.toggle('responsive-media--image', !isVideo(video) && isImage(image));
    element.classList.toggle('responsive-media--video', isVideo(video));

    if (isVideo(video)) {
      bindVideo(video);
      const playButton = createButton(
        element,
        '[data-video-play]',
        'responsive-media__video-play-button',
        getLabel('mediaPlayLabel', 'Play video'),
        '<span class="responsive-media__video-play-icon" data-video-play-icon aria-hidden="true"></span><span class="responsive-media__video-pause-icon" aria-hidden="true"><span></span><span></span></span>'
      );
      let iconWrapper = playButton.querySelector('[data-video-play-icon]');
      if (!isElement(iconWrapper)) {
        playButton.innerHTML = '<span class="responsive-media__video-play-icon" data-video-play-icon aria-hidden="true"></span><span class="responsive-media__video-pause-icon" aria-hidden="true"><span></span><span></span></span>';
        iconWrapper = playButton.querySelector('[data-video-play-icon]');
      }
      if (isElement(iconWrapper) && !iconWrapper.querySelector('img') && getLabel('mediaPlayIcon', '')) {
        const icon = document.createElement('img');
        icon.src = getLabel('mediaPlayIcon', '');
        icon.alt = '';
        iconWrapper.append(icon);
      }
      playButton.onclick = (event) => handleVideoPlayback(video, event);
      updateVideoToggle(element, video);

      const expandButton = createButton(
        element,
        '[data-video-expand]',
        'responsive-media__video-expand-button',
        getLabel('mediaExpandLabel', 'Open video full screen'),
        '<span aria-hidden="true">⛶</span>'
      );
      expandButton.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        openMediaDialog(video, expandButton);
      };
      if (!element.querySelector('[data-video-loader]')) {
        const loader = document.createElement('span');
        loader.className = 'responsive-media__video-loader';
        loader.dataset.videoLoader = '';
        loader.setAttribute('aria-hidden', 'true');
        element.append(loader);
      }
    } else if (isImage(image)) {
      element.querySelector('[data-video-play]')?.remove();
      element.querySelector('[data-video-expand]')?.remove();
      element.querySelector('[data-video-loader]')?.remove();
      if (element.closest('[data-media-gallery]')) bindMediaOpen(image);
    }
  };

  const render = (element) => {
    const output = element.querySelector('[data-responsive-media-output]');
    const desktopTemplate = element.querySelector('[data-responsive-media-template-desktop]');
    const mobileTemplate = element.querySelector('[data-responsive-media-template-mobile]');
    const template = mediaQuery.matches && mobileTemplate ? mobileTemplate : desktopTemplate;

    if (!output || !template) return;

    output.querySelectorAll('video').forEach(unbindVideo);
    output.querySelectorAll('img').forEach(unbindMediaOpen);
    output.querySelectorAll('video').forEach((video) => video.pause());
    output.replaceChildren(template.content.cloneNode(true));
    syncMediaControls(element);
  };

  const mount = (element) => {
    if (instances.has(element)) return;
    const handleChange = () => render(element);
    addMediaQueryListener(handleChange);
    instances.set(element, handleChange);
    render(element);
  };

  const unmount = (element) => {
    const handleChange = instances.get(element);
    if (handleChange) {
      removeMediaQueryListener(handleChange);
      instances.delete(element);
    }
    element.querySelectorAll('video').forEach((video) => {
      video.pause();
      unbindVideo(video);
    });
    element.querySelectorAll('img').forEach(unbindMediaOpen);
  };

  const initialize = (root) => {
    if (root.matches?.('[data-responsive-media]')) mount(root);
    root.querySelectorAll?.('[data-responsive-media]').forEach(mount);
    root.querySelectorAll?.('[data-pdp-video]').forEach((wrapper) => syncMediaControls(wrapper));
  };

  initialize(document);

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-responsive-media]')) unmount(event.target);
    event.target.querySelectorAll?.('[data-responsive-media]').forEach(unmount);
    event.target.querySelectorAll?.('[data-pdp-video]').forEach((wrapper) => {
      wrapper.querySelectorAll('video').forEach((video) => {
        video.pause();
        unbindVideo(video);
      });
    });
  });
})();
